import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTCoins, TCOINS_TYPES } from '../../context/TCoinsContext';
import { collection, addDoc, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import TCoinsPackages from './TCoinsPackages';
import PaymentProofViewer from '../Marketplace/PaymentProofViewer';
import { hasRole, ROLES } from '../../utils/roleManager';

const CoinsPurchase = ({ className = "", selectedPackage, onPackageSelected, onPackageSelectFromModal }) => {
  const { currentUser } = useAuth();
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentProof, setPaymentProof] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showPackages, setShowPackages] = useState(false);
  const [paymentSlip, setPaymentSlip] = useState(null);
  const [paymentSlipUrl, setPaymentSlipUrl] = useState('');
  const [selectedPackageName, setSelectedPackageName] = useState('');
  const [paymentConfig, setPaymentConfig] = useState({});

  useEffect(() => {
    if (currentUser) {
      loadPurchaseHistory();
      loadPaymentConfig();
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedPackage) {
      setPurchaseAmount(selectedPackage.tcoins.toString());
      setSelectedPackageName(selectedPackage.name);
      // Scroll to form after setting package
      setTimeout(() => {
        const formElement = document.querySelector('input[type="number"]');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          formElement.focus();
        }
      }, 100);
    } else {
      // Reset when package is cleared
      setSelectedPackageName('');
    }
  }, [selectedPackage]);

  const loadPurchaseHistory = async () => {
    if (!currentUser) return;
    
    try {
      const purchaseRef = collection(db, 'purchaseRequests');
      const q = query(
        purchaseRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const history = [];
      snapshot.forEach((doc) => {
        history.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setPurchaseHistory(history);
    } catch (error) {
      console.error('Error loading purchase history:', error);
    }
  };

  const loadPaymentConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'payment');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        setPaymentConfig(configSnap.data());
      }
    } catch (error) {
      console.error('Error loading payment config:', error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;
    
    try {
      const fileName = `payment-slips/${currentUser.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleSlipChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        alert('กรุณาเลือกไฟล์ภาพ (JPG, PNG)');
        return;
      }
      
      setPaymentSlip(file);
      
      // Preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPaymentSlipUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePurchaseRequest = async () => {
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบก่อน');
      return;
    }

    const amount = parseInt(purchaseAmount);
    if (!amount || amount <= 0) {
      alert('กรุณากรอกจำนวนเหรียญที่ถูกต้อง');
      return;
    }

    if (amount < 100) {
      alert('จำนวนเหรียญขั้นต่ำสำหรับการซื้อคือ 100 T Coins');
      return;
    }

    if (!paymentProof.trim() && !paymentSlip) {
      alert('กรุณากรอกหลักฐานการโอนเงินหรือแนบสลิป');
      return;
    }

    setIsProcessing(true);
    try {
      // Upload slip if exists
      let slipUrl = null;
      if (paymentSlip) {
        slipUrl = await handleFileUpload(paymentSlip);
      }

      // สร้างคำขอซื้อเหรียญ
      await addDoc(collection(db, 'purchaseRequests'), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName,
        amount: amount,
        totalPrice: selectedPackageName ? selectedPackage.price : amount, // ใช้ราคาแพ็คเกจหรือคำนวณจากจำนวนเหรียญ
        paymentMethod: paymentMethod,
        paymentMethodName: paymentConfig[paymentMethod]?.name || paymentMethod,
        paymentMethodIcon: paymentConfig[paymentMethod]?.icon || '💰',
        paymentProof: paymentProof,
        paymentSlipUrl: slipUrl,
        selectedPackage: selectedPackageName ? {
          name: selectedPackageName,
          price: selectedPackage.price,
          tcoins: selectedPackage.tcoins,
          bonus: selectedPackage.bonus,
          bonusType: selectedPackage.bonusType
        } : null,
        status: 'pending',
        type: 'purchase',
        createdAt: new Date()
      });

      alert('ส่งคำขอซื้อเหรียญสำเร็จ! แอดมินจะตรวจสอบและเติมเหรียญให้ภายใน 24 ชั่วโมง');
      setPurchaseAmount('');
      setPaymentProof('');
      setPaymentSlip(null);
      setPaymentSlipUrl('');
      loadPurchaseHistory();
    } catch (error) {
      console.error('Error creating purchase request:', error);
      alert('เกิดข้อผิดพลาดในการส่งคำขอซื้อเหรียญ');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'รอยืนยัน';
      case 'approved': return 'อนุมัติแล้ว';
      case 'rejected': return 'ปฏิเสธ';
      default: return 'ไม่ทราบ';
    }
  };

  const getPaymentMethodText = (method) => {
    return paymentConfig[method]?.name || method;
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-3 md:p-6 ${className}`}>
      <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">💰 ซื้อเหรียญ T Coins</h2>

      {/* ข้อมูลการซื้อ */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-4">ข้อมูลการซื้อเหรียญ</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-700 font-medium">อัตราแลกเปลี่ยน:</p>
            <p className="font-bold text-gray-900">1 บาท = 1 T Coin</p>
          </div>
          <div>
            <p className="text-gray-700 font-medium">ขั้นต่ำการซื้อ:</p>
            <p className="font-bold text-gray-900">100 T Coins</p>
          </div>
          <div>
            <p className="text-gray-700 font-medium">วิธีการชำระเงิน:</p>
            <p className="font-bold text-gray-900">โอนเงิน/พร้อมเพย์</p>
          </div>
          <div>
            <p className="text-gray-700 font-medium">เวลาดำเนินการ:</p>
            <p className="font-bold text-gray-900">ภายใน 24 ชั่วโมง</p>
          </div>
        </div>
      </div>

      {/* ฟอร์มซื้อเหรียญ */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">ส่งคำขอซื้อเหรียญ</h3>
        
        {/* ปุ่มเลือกแพ็คเกจ */}
        <div className="mb-6 text-center">
          <button
            onClick={() => setShowPackages(true)}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition font-medium"
          >
            🎁 เลือกแพ็คเกจเหรียญ (ราคาพิเศษ!)
          </button>
          <p className="text-sm text-gray-600 mt-2">หรือกรอกจำนวนเหรียญที่ต้องการด้านล่าง</p>
        </div>

        {/* แจ้งเตือนเมื่อเลือกแพ็คเกจแล้ว */}
        {selectedPackageName && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-green-600 mr-2">✅</span>
                <p className="text-sm text-green-800 font-medium">
                  เลือกแพ็คเกจ "{selectedPackageName}" แล้ว กรุณากรอกข้อมูลการชำระเงินด้านล่าง
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedPackageName('');
                  setPurchaseAmount('');
                  if (onPackageSelected) {
                    onPackageSelected();
                  }
                }}
                className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition"
              >
                เปลี่ยนแพ็คเกจ
              </button>
            </div>
          </div>
        )}

        {/* แสดงข้อมูลแพ็คเกจที่เลือก */}
        {selectedPackageName && (
          <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-xl shadow-lg">
            {/* Header - เน้นเงินที่ต้องจ่าย */}
            <div className="text-center mb-6">
              <h4 className="text-2xl font-bold text-green-800 mb-2">แพ็คเกจที่เลือก: {selectedPackageName}</h4>
              <div className="bg-green-600 text-white px-8 py-4 rounded-lg inline-block">
                <div className="text-3xl font-bold">฿{selectedPackage?.price?.toLocaleString() || '0'}</div>
                <div className="text-sm opacity-90">ราคาที่ต้องจ่ายจริง</div>
              </div>
            </div>
            
            {/* ข้อมูลรายละเอียด - แสดงเป็นแถวเดียว */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white p-4 rounded-lg border border-blue-200 text-center">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-blue-600 mr-2">🪙</span>
                  <span className="font-semibold text-gray-800">T Coins หลัก</span>
                </div>
                <div className="text-xl font-bold text-blue-600">
                  {selectedPackage?.tcoins?.toLocaleString() || '0'} T Coins
                </div>
                <div className="text-xs text-gray-600">แลกกลับเป็นเงินสดได้</div>
              </div>
              
              {selectedPackage?.bonus > 0 && (
                <div className="bg-white p-4 rounded-lg border border-red-200 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-red-600 mr-2">🎁</span>
                    <span className="font-semibold text-gray-800">โบนัส</span>
                  </div>
                  <div className="text-xl font-bold text-red-600">
                    +{selectedPackage.bonus.toLocaleString()} Bonus Coins
                  </div>
                  <div className="text-xs text-gray-600">
                    โบนัสพิเศษ
                  </div>
                </div>
              )}
              
              <div className="bg-white p-4 rounded-lg border border-purple-200 text-center">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-purple-600 mr-2">📊</span>
                  <span className="font-semibold text-gray-800">รวมทั้งหมด</span>
                </div>
                <div className="text-xl font-bold text-purple-600">
                  {((selectedPackage?.tcoins || 0) + (selectedPackage?.bonus || 0)).toLocaleString()} T Coins
                </div>
                <div className="text-xs text-gray-600">จำนวนเหรียญที่คุณจะได้รับ</div>
              </div>
            </div>
            
            {/* ข้อความเน้นย้ำ */}
            <div className="mt-4 p-4 bg-yellow-100 border border-yellow-300 rounded-lg text-center">
              <div className="flex items-center justify-center">
                <span className="text-yellow-600 mr-2">💡</span>
                <span className="text-sm text-yellow-800 font-medium">
                  คุณจะได้รับทั้งหมด {((selectedPackage?.tcoins || 0) + (selectedPackage?.bonus || 0)).toLocaleString()} T Coins
                </span>
                <span className="text-sm text-green-600 font-bold ml-2">
                  จ่ายเพียง ฿{selectedPackage?.price?.toLocaleString() || '0'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              วิธีการชำระเงิน
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800"
            >
              {Object.keys(paymentConfig).length > 0 ? (
                Object.entries(paymentConfig)
                  .filter(([methodId, method]) => method.enabled)
                  .map(([methodId, method]) => (
                    <option key={methodId} value={methodId}>
                      {method.icon} {method.name}
                    </option>
                  ))
              ) : (
                <>
                  <option value="bank_transfer">🏦 โอนเงินผ่านธนาคาร</option>
                  <option value="promptpay">📱 พร้อมเพย์</option>
                  <option value="true_wallet">💳 ทรูวอลเล็ท</option>
                </>
              )}
            </select>
            
            {/* ข้อมูลการชำระเงิน */}
            <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-sm font-semibold text-green-800 mb-2">💳 ข้อมูลการชำระเงิน</h4>
              {paymentConfig[paymentMethod] ? (
                <div className="text-sm text-green-700">
                  <p className="font-medium">{paymentConfig[paymentMethod].icon} {paymentConfig[paymentMethod].name}</p>
                  
                  {paymentConfig[paymentMethod].type === 'bank' && (
                    <>
                      <p>ธนาคาร: <span className="font-bold">{paymentConfig[paymentMethod].bankName}</span></p>
                      <p>เลขที่บัญชี: <span className="font-mono font-bold">{paymentConfig[paymentMethod].accountNumber}</span></p>
                      <p>ชื่อบัญชี: <span className="font-bold">{paymentConfig[paymentMethod].accountName}</span></p>
                    </>
                  )}
                  
                  {(paymentConfig[paymentMethod].type === 'mobile' || paymentConfig[paymentMethod].type === 'wallet') && (
                    <>
                      <p>เบอร์โทร: <span className="font-mono font-bold">{paymentConfig[paymentMethod].phoneNumber}</span></p>
                      <p>ชื่อ: <span className="font-bold">{paymentConfig[paymentMethod].accountName}</span></p>
                    </>
                  )}
                  
                  {paymentConfig[paymentMethod].type === 'other' && (
                    <>
                      <p>ชื่อ: <span className="font-bold">{paymentConfig[paymentMethod].accountName}</span></p>
                      <p>หมายเลข: <span className="font-mono font-bold">{paymentConfig[paymentMethod].accountNumber}</span></p>
                    </>
                  )}
                  
                  {/* Display QR Code if exists */}
                  {paymentConfig[paymentMethod].qrCodeUrl && (
                    <div className="mt-3 pt-3 border-t border-green-300">
                      <p className="text-xs font-medium text-green-800 mb-2 text-center">📱 สแกน QR Code เพื่อโอนเงิน</p>
                      <div className="flex justify-center">
                        <img
                          src={paymentConfig[paymentMethod].qrCodeUrl}
                          alt="QR Code"
                          className="w-48 h-48 object-contain border-2 border-green-400 rounded-lg bg-white p-2"
                        />
                      </div>
                      <p className="text-xs text-green-600 mt-2 text-center">สแกน QR Code นี้เพื่อโอนเงินอัตโนมัติ</p>
                    </div>
                  )}
                  
                  <p className="text-xs text-green-600 mt-1">⚠️ กรุณาโอนเงินตามจำนวนที่ระบุ</p>
                </div>
              ) : (
                <div className="text-sm text-green-700">
                  {paymentMethod === 'bank_transfer' && (
                    <>
                      <p className="font-medium">🏦 ธนาคารกรุงไทย</p>
                      <p>เลขที่บัญชี: <span className="font-mono font-bold">123-4-56789-0</span></p>
                      <p>ชื่อบัญชี: <span className="font-bold">บริษัท ฮักกัน จำกัด</span></p>
                    </>
                  )}
                  {paymentMethod === 'promptpay' && (
                    <>
                      <p className="font-medium">📱 พร้อมเพย์</p>
                      <p>เบอร์โทร: <span className="font-mono font-bold">081-234-5678</span></p>
                      <p>ชื่อ: <span className="font-bold">บริษัท ฮักกัน จำกัด</span></p>
                    </>
                  )}
                  {paymentMethod === 'true_wallet' && (
                    <>
                      <p className="font-medium">💳 ทรูวอลเล็ท</p>
                      <p>เบอร์โทร: <span className="font-mono font-bold">081-234-5678</span></p>
                      <p>ชื่อ: <span className="font-bold">บริษัท ฮักกัน จำกัด</span></p>
                    </>
                  )}
                  <p className="text-xs text-green-600 mt-1">⚠️ กรุณาโอนเงินตามจำนวนที่ระบุ</p>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              หลักฐานการโอนเงิน
            </label>
            
            {/* Text input for proof */}
            <textarea
              value={paymentProof}
              onChange={(e) => setPaymentProof(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
              rows={2}
              placeholder="ระบุเลขที่อ้างอิง, เวลาโอน, หรือรายละเอียดการโอนเงิน"
              style={{ color: '#374151' }}
            />
            
            {/* File upload for slip */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <div className="text-center">
                <input
                  type="file"
                  id="payment-slip"
                  accept="image/*"
                  onChange={handleSlipChange}
                  className="hidden"
                />
                <label
                  htmlFor="payment-slip"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="text-4xl mb-2">📷</div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    แนบสลิปการโอนเงิน
                  </p>
                  <p className="text-xs text-gray-500">
                    คลิกเพื่อเลือกไฟล์ภาพ (JPG, PNG) ขนาดไม่เกิน 5MB
                  </p>
                </label>
                
                {paymentSlipUrl && (
                  <div className="mt-4">
                    <PaymentProofViewer
                      paymentProofUrl={paymentSlipUrl}
                      alt="Payment Slip Preview"
                      showLabel={true}
                      size="md"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentSlip(null);
                        setPaymentSlipUrl('');
                        document.getElementById('payment-slip').value = '';
                      }}
                      className="mt-2 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition"
                    >
                      ลบภาพ
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-xs text-gray-600 mt-2">
              💡 สามารถกรอกข้อความหรือแนบสลิป หรือทำทั้งสองอย่าง
            </p>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handlePurchaseRequest}
            disabled={isProcessing || !purchaseAmount || (!paymentProof && !paymentSlip) || parseInt(purchaseAmount) < 100}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition font-medium"
          >
            {isProcessing ? 'กำลังส่งคำขอ...' : 'ส่งคำขอซื้อเหรียญ'}
          </button>
        </div>
      </div>

      {/* ประวัติการซื้อเหรียญ */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">ประวัติการซื้อเหรียญ</h3>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-medium"
          >
            {showHistory ? 'ซ่อน' : 'แสดง'} ประวัติ
          </button>
        </div>

        {showHistory && (
          <>
            {purchaseHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-700">
                <p className="font-medium">ไม่มีประวัติการซื้อเหรียญ</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        จำนวนเหรียญ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ราคา
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        วิธีชำระ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        สถานะ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        วันที่ขอ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {purchaseHistory.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.amount} T Coins
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <p className="text-green-600 font-semibold">
                            {request.totalPrice.toLocaleString()} บาท
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getPaymentMethodText(request.paymentMethod)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                            {getStatusText(request.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || 'ไม่ทราบ'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* คำแนะนำ */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
        <h4 className="text-lg font-semibold text-yellow-800 mb-2">💡 คำแนะนำ</h4>
        <div className="space-y-3">
          <div className="text-sm text-yellow-800 font-medium">
            <h5 className="font-semibold mb-2">📋 ขั้นตอนการซื้อเหรียญ:</h5>
            <ul className="space-y-1">
              <li>• โอนเงินตามจำนวนที่ระบุ แล้วส่งหลักฐานการโอน</li>
              <li>• แอดมินจะตรวจสอบและเติมเหรียญภายใน 24 ชั่วโมง</li>
              <li>• หากมีข้อสงสัยสามารถติดต่อแอดมินได้</li>
            </ul>
          </div>
          
          <div className="text-sm text-yellow-800 font-medium">
            <h5 className="font-semibold mb-2">💰 การใช้ T Coins และ Bonus Coins:</h5>
            <ul className="space-y-1">
              {hasRole(currentUser, ROLES.USER) || hasRole(currentUser, ROLES.FARM) ? (
                // Marketplace User - ข้อความสำหรับตลาดซื้อขาย
                <>
                  <li>• ใช้สำหรับซื้อสินค้าในตลาดค้าส่ง</li>
                  <li>• ใช้สำหรับจ่ายค่าธรรมเนียมการขายสินค้า</li>
                  <li>• ใช้สำหรับเข้าร่วมประมูลและซื้อสินค้าประมูล</li>
                  <li>• สามารถแลกเหรียญกลับเป็นเงินได้เมื่อเกิน 300 T Coins</li>
                  <li className="text-red-600 font-semibold">• Bonus Coins สงวนสิทธิ์ใช้งานในระบบเท่านั้น แลกเป็นสดเงินไม่ได้</li>
                </>
              ) : (
                // Non-Marketplace User - ข้อความเดิม (ซ่อนข้อความเกี่ยวกับน้องและโม)
                <>
                  <li>• ใช้สำหรับซื้อของขวัญและจองคิวในระบบ</li>
                  <li>• สามารถแลกเหรียญกลับเป็นเงินได้เมื่อเกิน 300 T Coins</li>
                  <li className="text-red-600 font-semibold">• Bonus Coins สงวนสิทธิ์ใช้งานในระบบเท่านั้น แลกเป็นสดเงินไม่ได้</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Packages Modal - Mobile Optimized */}
      {showPackages && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 md:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPackages(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button - Top Right Corner */}
            <button
              onClick={() => setShowPackages(false)}
              className="absolute top-2 right-2 md:top-3 md:right-3 text-gray-500 hover:text-gray-700 transition-colors p-1 z-10 bg-white rounded-full shadow-md"
              title="ปิด"
            >
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <TCoinsPackages 
              onClose={() => setShowPackages(false)}
              onSelectPackage={(packageData) => {
                // Set package data for this component
                setPurchaseAmount(packageData.tcoins.toString());
                setSelectedPackageName(packageData.name || 'แพ็คเกจที่เลือก');
                
                // Also notify parent if callback exists (to update selectedPackageData in TCoinsWallet)
                if (onPackageSelectFromModal) {
                  onPackageSelectFromModal(packageData);
                }
                
                setShowPackages(false);
                
                // Scroll to payment section after selecting package
                setTimeout(() => {
                  const paymentSection = document.querySelector('.bg-gradient-to-r.from-green-50.to-blue-50');
                  if (paymentSection) {
                    paymentSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CoinsPurchase;
